import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiTrendingUp, FiDollarSign, FiPercent, FiHome } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const benefits = [
  {
    icon: <FiTrendingUp className="size-6" />,
    title: "Portfolio growth",
    description:
      "Structure your loans correctly to maximise borrowing capacity and build your portfolio faster.",
  },
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Tax benefits",
    description:
      "Investment loans can offer tax advantages. We'll help you structure your finances effectively.",
  },
  {
    icon: <FiPercent className="size-6" />,
    title: "Interest-only options",
    description:
      "Improve cash flow with interest-only periods while your property (ideally) grows in value.",
  },
  {
    icon: <FiHome className="size-6" />,
    title: "Equity leverage",
    description:
      "Use equity in existing properties to fund deposits for new investments without touching savings.",
  },
];

const loanTypes = [
  {
    title: "Principal & Interest",
    description:
      "Pay down the loan over time. Lower interest costs overall, builds equity faster.",
    bestFor: "Long-term investors, those wanting to reduce debt",
  },
  {
    title: "Interest Only",
    description:
      "Only pay interest for a set period (usually 1-5 years). Lower repayments, better cash flow.",
    bestFor: "Cash flow focus, tax deduction maximisation",
  },
  {
    title: "Line of Credit",
    description:
      "Flexible access to equity. Draw down and repay as needed, only pay interest on what you use.",
    bestFor: "Experienced investors, those needing flexibility",
  },
  {
    title: "Fixed Rate",
    description:
      "Lock in your rate for certainty. Protects against rate rises but less flexible.",
    bestFor: "Budget certainty, rate rise protection",
  },
];

const considerations = [
  {
    title: "Loan structure",
    points: [
      "Keep investment debt separate from personal debt",
      "Consider cross-collateralisation implications",
      "Interest-only vs principal & interest trade-offs",
    ],
  },
  {
    title: "Deposit & equity",
    points: [
      "Typically need 10-20% deposit for investment loans",
      "Can use equity from existing properties",
      "LMI applies if borrowing more than 80% LVR",
    ],
  },
  {
    title: "Cash flow",
    points: [
      "Factor in vacancy periods and maintenance costs",
      "Consider interest rate buffer for rate rises",
      "Rental income assessment varies by lender",
    ],
  },
  {
    title: "Tax implications",
    points: [
      "Interest on investment loans is typically tax deductible",
      "Negative gearing can offset other income",
      "Consult your accountant for specific advice",
    ],
  },
];

export function InvestmentPropertyPage() {
  return (
    <>
      <SEO
        title="Investment Property Loans Brisbane & Springfield"
        description="Investment property loans for Brisbane, Springfield, Logan & Ipswich investors. Interest-only options, equity release, portfolio structuring across 70+ lenders."
        url="/home-loans/investment"
        keywords={[
          "investment property loan Brisbane",
          "investment loan Springfield",
          "investment property finance",
          "interest only loan",
          "equity release home loan",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Investment Property Loans",
            description:
              "Investment property finance — interest-only loans, equity release, portfolio lending and structuring from 70+ lenders.",
            url: "/home-loans/investment",
            serviceType: "Investment Property Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "Investment Property", url: "/home-loans/investment" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/home-loans"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <FiArrowRight className="size-4 mr-2 rotate-180" />
                Home Loans
              </Link>
              <p className="section-label mb-4">Investment Property</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Build your property portfolio
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Whether you're buying your first investment property or expanding
                your portfolio, the right loan structure makes all the difference.
                We'll help you maximise borrowing power and optimise cash flow.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Discuss Your Strategy
                  <FiArrowRight className="size-4" />
                </Link>
                <Link
                  to="/calculators/borrowing-power"
                  className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  Check Borrowing Power
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="bg-white p-5 rounded-2xl border border-border/50"
                >
                  <div className="p-2 bg-accent/20 rounded-lg inline-flex mb-3 text-primary">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{benefit.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loan Types */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Loan Options</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Investment loan types
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Different loan structures suit different strategies. We'll help you
              choose the right option for your situation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {loanTypes.map((type) => (
              <div
                key={type.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold text-lg mb-2">{type.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {type.description}
                </p>
                <div className="bg-muted/50 rounded-lg px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    Best for:{" "}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {type.bestFor}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Considerations */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Things to Consider</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Investment property considerations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {considerations.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold mb-4">{item.title}</h3>
                <ul className="space-y-2">
                  {item.points.map((point) => (
                    <li
                      key={point}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary flex-shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">
                How We Help
              </p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Investment property specialists
              </h2>
              <p className="text-primary-foreground/80 mb-8">
                We understand the unique needs of property investors. From structuring
                your first investment loan to managing a multi-property portfolio,
                we provide expert guidance at every stage.
              </p>
              <ul className="space-y-4">
                {[
                  "Assess your borrowing capacity across multiple lenders",
                  "Structure loans to maximise tax efficiency and cash flow",
                  "Leverage equity in existing properties for new purchases",
                  "Compare interest-only and principal & interest options",
                  "Ongoing support as your portfolio grows",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-3xl p-8">
              <p className="text-primary-foreground text-sm font-medium mb-4">Did you know?</p>
              <h3 className="font-serif text-2xl mb-4">
                Lender policies vary significantly for investors
              </h3>
              <p className="text-primary-foreground/80">
                Some lenders are more investor-friendly than others. Assessment of
                rental income, acceptable LVRs, and interest-only terms all vary.
                We know which lenders suit which situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to invest?"
        title="Let's discuss your investment strategy"
        description="Book a free consultation and we'll help you structure your next property purchase."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
